import { Injectable, OnDestroy, inject } from '@angular/core';

import { RewardTargetType } from '@bpos/v1/sports-promo/tokens';
import { ApiService } from '@frontend/sports/common/api-utils';
import { CrmConfig } from '@frontend/sports/common/client-config-data-access';
import { SimpleCallback } from '@frontend/sports/common/core/utils/extended-types';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { UserService } from '@frontend/sports/user/feature';
import { EventsService, SimpleEvent, VanillaEventNames } from '@frontend/vanilla/core';
import { BehaviorSubject, ReplaySubject, filter } from 'rxjs';

import { IRewardTokenData, RewardTokenType } from '../tokens-base/token-base.models';
import { CrmOfferLoaderRequestParamsBaseService } from './crm-offer-loader-request-params-base.service';
import { CrmOfferPreviewService } from './crm-offer-preview.service';
import { Campaign, OffersResponse } from './crm-offer.server.model';

@Injectable({
    providedIn: 'root',
})
export class CrmOfferLoaderService implements OnDestroy {
    private apiService = inject(ApiService);
    private crmConfig = inject(CrmConfig);
    private userService = inject(UserService);
    private timerService = inject(TimerService);
    private eventsService = inject(EventsService);
    private crmOfferPreviewService = inject(CrmOfferPreviewService);
    private crmOfferLoaderRequestParamsService = inject(CrmOfferLoaderRequestParamsBaseService, { optional: true });
    private productUtils = inject(SportsProductUtils);

    private _promotions$ = new ReplaySubject<Campaign[]>(1);
    private _tokens$ = new ReplaySubject<IRewardTokenData[]>(1);
    private _promoRestricted$ = new BehaviorSubject<boolean>(false);

    readonly promotions$ = this._promotions$.asObservable();
    readonly tokens$ = this._tokens$.asObservable();
    readonly promoRestricted$ = this._promoRestricted$.asObservable();

    private interval?: SimpleCallback;

    constructor() {
        if (!this.crmConfig.promotions.isEnabled) {
            this._promotions$.next([]);
        }

        if (!this.crmConfig.tokens.isEnabled) {
            this._tokens$.next([]);
        }

        if (this.crmConfig.promotions.isEnabled || this.crmConfig.tokens.isEnabled) {
            this.crmOfferPreviewService.inPreview ? this.fetchOfferPreview() : this.startGettingOffers();
        }
    }

    ngOnDestroy(): void {
        this.stopRefresh();
    }

    refresh(forceRefresh: boolean = true): void {
        this.fetchOffer(forceRefresh);
    }

    clearAndRefresh(): void {
        this._promotions$.next([]);
        this.fetchOffer();
    }

    private startGettingOffers(forceRefresh: boolean = false): void {
        if (this.userService.isAuthenticated) {
            this.setupOfferRefresh(forceRefresh);
        } else {
            this._tokens$.next([]);
        }

        this.userService.onUserLogin$.pipe(this.productUtils.filterSportsEmitLast()).subscribe(() => this.setupOfferRefresh());

        this.userService.onUserLogout$.pipe(this.productUtils.filterSportsEmitLast()).subscribe(() => {
            this._promotions$.next([]);
            this._tokens$.next([]);
            this.stopRefresh();
        });

        this.eventsService.events
            .pipe(
                this.productUtils.filterSportsEmitLast(),
                filter((e: SimpleEvent) => e?.eventName === VanillaEventNames.RefreshCrmOffers),
            )
            .subscribe(() => this.refresh());
    }

    private fetchOffer(forceRefresh: boolean = false): void {
        this.apiService.get<OffersResponse>('crmOffers', this.crmOfferLoaderRequestParamsService?.params).subscribe((result) => {
            if (!forceRefresh && this.crmConfig.promotions.isEnabled) {
                this._promotions$.next(result.campaigns);
            }

            if (this.crmConfig.tokens.isEnabled) {
                this._tokens$.next(this.getTokensResponse(result));
            }

            if (this.crmConfig.promotions.isRegulatoryRestricted) {
                this._promoRestricted$.next(result.isPromoOfferRestricted);
            }

            if (result.cancelPolling) {
                this.stopRefresh();
            }
        });
    }

    private fetchOfferPreview(): void {
        if (!this.crmConfig.tokens.isEnabled) {
            return;
        }

        this.apiService.get<OffersResponse>('crmOffers/GetOfferPreview', this.crmOfferPreviewService.offerParams).subscribe((result) => {
            this._tokens$.next(this.getTokensResponse(result));
        });
    }

    private getTokensResponse(response: OffersResponse): IRewardTokenData[] {
        const oddsBoostTokens: IRewardTokenData[] = response.oddsBoostTokens.map((token) => ({
            ...token,
            rewardTokenType: RewardTokenType.OddsBoost,
        }));

        const riskFreeTokens: IRewardTokenData[] = response.riskFreeTokens.map((token) => ({
            ...token,
            rewardTokenType: RewardTokenType.RiskFreeBet,
        }));

        const freeBetTokens: IRewardTokenData[] = response.freeBetTokens.map((token) => ({
            ...token,
            rewardTokenType: RewardTokenType.FreeBet,
        }));

        const betAndGetTokens: IRewardTokenData[] = response.betAndGetTokens.map((token) => ({
            ...token,
            rewardTokenType: RewardTokenType.BetAndGet,
        }));

        const rewardTokens = [...oddsBoostTokens, ...riskFreeTokens, ...freeBetTokens, ...betAndGetTokens];

        if (!this.crmConfig.tokens.acquisitionRewardDetails.isWelcomeOfferTokenAllowed) {
            return rewardTokens.filter((x) => x.rewardTargetType !== RewardTargetType.WelcomeOffer);
        }

        return rewardTokens;
    }

    private setupOfferRefresh(forceRefresh: boolean = false): void {
        this.fetchOffer(forceRefresh);

        if (this.crmConfig.promotions.refreshInterval && !forceRefresh) {
            this.interval = this.timerService.setInterval(() => this.fetchOffer(), this.crmConfig.promotions.refreshInterval);
        }
    }

    private stopRefresh(): void {
        if (this.interval) {
            this.interval();
        }
    }
}
